import { Accordion } from "@/components/ui/accordion";

import { twMerge } from "tailwind-merge";

import DatasetAttributeItem from "./DatasetAttributeItem";
import { DataWeightSettingsValue } from "./useDataWeightSettings";

interface DatasetAccordionProps extends DataWeightSettingsValue {
  showAdvancedColumns: boolean;
  className?: string;
  /** When true, rows for datasets added after the saved baseline get a red
   * border. Only the advanced panel enables this. */
  highlightWeightAdjusted?: boolean;
}

export default function DatasetAccordion({
  showAdvancedColumns,
  className,
  highlightWeightAdjusted = false,
  state,
  actions,
  derived
}: Readonly<DatasetAccordionProps>) {
  const { localWeightValues, selectedScoringOption, hasAttemptedAdvancedSave } =
    state;
  const { dispatch } = actions;
  const {
    visualizationOrder,
    availableDatasets,
    weightAdjustedDatasetIds,
    datasetWeightPercentages,
    allAttributeGlobalPercentages,
    isAllAttributeWeightZero,
    isAllDatasetWeightZero
  } = derived;

  return (
    <Accordion
      type="multiple"
      defaultValue={visualizationOrder}
      className={
        className ??
        twMerge(
          "flex flex-col",
          showAdvancedColumns
            ? "gap-2.5"
            : "gap-4 divide-y-8 divide-white-smoke"
        )
      }
    >
      {/* The order still holds deleted and unpublished datasets, which have no
          weights and aren't in availableDatasets */}
      {visualizationOrder
        .filter((datasetId) => availableDatasets[datasetId])
        .map((datasetId: string) => (
          <DatasetAttributeItem
            key={datasetId}
            dataset={availableDatasets[datasetId]}
            isWeightAdjusted={
              highlightWeightAdjusted &&
              weightAdjustedDatasetIds.includes(datasetId)
            }
            datasetAttributesWeights={Object.fromEntries(
              Object.entries(
                localWeightValues[datasetId]?.attributes || {}
              ).filter(([, value]) => value !== undefined)
            )}
            datasetWeightValue={localWeightValues[datasetId]?.weight}
            datasetWeightPercentage={datasetWeightPercentages[datasetId]}
            attributeGlobalPercentages={
              allAttributeGlobalPercentages[datasetId] || {}
            }
            datasetIsIncludedScoring={
              localWeightValues[datasetId]?.include_scoring !== false
            }
            dispatchWeightSettings={dispatch}
            isAllAttributeWeightZero={isAllAttributeWeightZero}
            isAllDatasetWeightZero={isAllDatasetWeightZero}
            selectedScoringOption={selectedScoringOption}
            showAdvancedColumns={showAdvancedColumns}
            showAdvancedValidationErrors={hasAttemptedAdvancedSave}
          />
        ))}
    </Accordion>
  );
}
